import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  Building2,
  Code2,
  Store,
  TrendingUp,
  LineChart,
  Handshake,
  Palette,
} from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/29849777/pexels-photo-29849777.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

const SECTORS = [
  { icon: Zap, name: "Energy", note: "Solar, wind, storage, CBG, grid" },
  { icon: Building2, name: "Infrastructure", note: "Hard assets, utilities, logistics" },
  { icon: Code2, name: "Software Implementation", note: "Platforms, integration, operations tech" },
  { icon: Store, name: "Retail", note: "Consumer, distribution, networks" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-end">
            <div className="md:col-span-7 saafi-fade-up">
              <div className="flex items-center gap-3 mb-10">
                <span className="inline-block w-2 h-2 bg-[#A3C940]" />
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#575B57]">
                  Consulting · Investment · Execution
                </span>
              </div>
              <h1 className="font-display text-[44px] sm:text-6xl lg:text-[84px] leading-[0.95] tracking-tighter font-medium text-[#1A1C1A]">
                Capital, climate <br className="hidden sm:block" />
                and industry — <em className="not-italic text-[#0F7A3E]">rebuilt</em> <br className="hidden sm:block" />
                with intent.
              </h1>
              <p className="mt-10 max-w-xl text-lg leading-relaxed text-[#575B57]">
                Saafi Industries is a consulting and investment firm operating at
                the intersection of environmental sustainability, energy
                infrastructure, and business transformation — partnering with
                companies, investors and institutions to design, build and scale
                solutions that are commercially viable and environmentally
                responsible.
              </p>
            </div>

            <div className="md:col-span-5 relative">
              <div className="relative overflow-hidden aspect-[3/4] w-full">
                <img
                  src={HERO_IMG}
                  alt="Modern building façade reflecting sustainable architecture"
                  className="absolute inset-0 w-full h-full object-cover saafi-img-reveal"
                />
                <div className="absolute inset-0 bg-[#1A1C1A]/10 pointer-events-none" />
              </div>
              <div className="hidden md:block absolute -bottom-10 -left-10 bg-[#FBFBF9] border border-[#E4E5DF] px-6 py-5 max-w-[240px]">
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-2">
                  Field note
                </p>
                <p className="text-sm leading-snug text-[#1A1C1A]">
                  "Every megawatt deployed and every ton diverted is a commercial
                  decision first — and a climate one by design."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom meta row */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-[#E4E5DF] pt-10">
            <MetaCell
              className="md:col-span-2"
              k="Sectors"
              lines={["4 Core Verticals"]}
            />
            <MetaCell
              className="md:col-span-5"
              k="Engagements"
              lines={[
                "Advisory",
                "Software design",
                "Software implementation",
                "Investment",
              ]}
            />
            <MetaCell
              className="md:col-span-2"
              k="Geographies"
              lines={["India"]}
            />
            <MetaCell
              className="md:col-span-3"
              k="Style"
              lines={["Execution-backed"]}
            />
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
              01 — Philosophy
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight font-medium">
              Not advisory. <br /> <em className="not-italic text-[#0F7A3E]">Execution-backed</em> consulting.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed text-[#575B57]">
              The gap between a good strategy deck and a built, operating asset
              is where most climate capital disappears. We close that gap.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-[#575B57]">
              Our teams sit inside operating rooms, commissioning sites, and
              investment committees — translating thesis into infrastructure,
              and infrastructure into measurable outcomes. We take accountability
              for results, not just recommendations.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              <div className="border-t border-[#1A1C1A] pt-5">
                <p className="font-display text-4xl tracking-tight">5+</p>
                <p className="text-sm text-[#575B57] mt-2">Years operating at the industrial frontier</p>
              </div>
              <div className="border-t border-[#1A1C1A] pt-5">
                <p className="font-display text-4xl tracking-tight">2</p>
                <p className="text-sm text-[#575B57] mt-2">Continents of active engagements</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW — TETRIS BENTO */}
      <section className="bg-[#F3F3E8]/60 border-y border-[#E4E5DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
                02 — What We Do
              </p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight font-medium max-w-2xl">
                Four capabilities, one operating system for industrial growth.
              </h2>
            </div>
            <Link
              to="/services"
              className="hidden md:inline-flex items-center gap-2 text-sm text-[#0F7A3E] saafi-link"
              data-testid="home-services-link"
            >
              All services <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <CapabilityCard
              span="md:col-span-7 md:row-span-2"
              tall
              icon={Code2}
              eyebrow="Software Implementation"
              title="We ship the software, then keep it running."
              body="ERP, asset-management, SCADA / IoT, analytics and workflow platforms — engineered, integrated, commissioned and supported — with accountability for adoption, uptime and outcomes."
              bullets={[
                "ERP & asset mgmt rollouts",
                "SCADA / IoT integration",
                "Data & analytics platforms",
                "Workflow & automation",
              ]}
            />
            <CapabilityCard
              span="md:col-span-5"
              icon={Handshake}
              eyebrow="Advisory"
              title="Advisory with a delivery mandate."
              body="We advise where the decisions get made — strategy, technology, financing, regulatory — and stay through commissioning."
            />
            <CapabilityCard
              span="md:col-span-5"
              icon={Palette}
              eyebrow="Software design"
              title="Designed around the operating reality."
              body="Architecture, data models, UX and integration contracts — built with the operators who will run the system, not just the people who bought it."
            />
            <CapabilityCard
              span="md:col-span-5"
              icon={LineChart}
              eyebrow="Investment"
              title="Patient, operator-grade capital."
              body="We invest our own balance sheet alongside aligned partners into companies and assets building the industrial backbone of the transition."
            />
            <CapabilityCard
              span="md:col-span-7"
              icon={TrendingUp}
              eyebrow="And we stay in"
              title="Execution-backed, not deck-backed."
              body="Saafi's capabilities are modular, but our default end-state is the same: a built, running asset — and a software platform actually being used to operate it."
            />
          </div>
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
              03 — Industries
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight font-medium">
              Four sectors. <br /> One operating conviction.
            </h2>
            <p className="mt-8 text-lg text-[#575B57] leading-relaxed">
              We go deep where capital, infrastructure, software and consumer
              complexity converge — and stay long enough to move the metrics
              that matter.
            </p>
            <Link
              to="/industries"
              className="mt-10 inline-flex items-center gap-2 text-sm text-[#0F7A3E] saafi-link"
              data-testid="home-industries-link"
            >
              Explore all industries <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-l border-[#E4E5DF]">
            {SECTORS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.name}
                  data-testid={`sector-tile-${s.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="group p-8 md:p-10 border-b border-r border-[#E4E5DF] bg-white hover:bg-[#F3F3E8] transition-colors duration-300"
                >
                  <Icon className="w-7 h-7 text-[#0F7A3E] mb-8" strokeWidth={1.25} />
                  <p className="font-display text-2xl tracking-tight text-[#1A1C1A]">
                    {s.name}
                  </p>
                  <p className="text-sm text-[#575B57] mt-2">{s.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function CapabilityCard({ span, tall, icon: Icon, eyebrow, title, body, bullets }) {
  return (
    <div
      data-testid={`capability-card-${eyebrow.toLowerCase().replace(/\s/g, "-")}`}
      className={`${span} group bg-white border border-[#E4E5DF] p-8 md:p-12 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(31,78,61,0.25)] flex flex-col justify-between ${
        tall ? "min-h-[420px]" : "min-h-[300px]"
      }`}
    >
      <div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B]">
            {eyebrow}
          </p>
          <Icon className="w-6 h-6 text-[#0F7A3E]" strokeWidth={1.25} />
        </div>
        <h3 className="mt-10 font-display text-2xl md:text-3xl leading-snug tracking-tight text-[#1A1C1A]">
          {title}
        </h3>
        <p className="mt-6 text-[15px] leading-relaxed text-[#575B57]">{body}</p>
        {bullets && (
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-[#1A1C1A]">
                <span className="inline-block w-1.5 h-1.5 mt-2 bg-[#A3C940]" />
                {b}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function MetaCell({ className = "", k, lines }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-3">
        {k}
      </p>
      <ul className="space-y-1.5">
        {lines.map((l) => (
          <li
            key={l}
            className="font-display text-base md:text-lg tracking-tight text-[#1A1C1A] leading-snug"
          >
            {l}
          </li>
        ))}
      </ul>
    </div>
  );
}
